/**
 * RepConnect - Admin Login Component
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff, Shield, LogIn } from 'lucide-react';
import { UserStorage } from '../utils/userStorage';
import AdminDelegation from '../utils/adminDelegation';
import { logger } from '../utils/logger';

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Find user by email and userType admin
      const allUsers = UserStorage.getAllUsers();
      const adminUser = allUsers.find(user => 
        user.email === email && 
        user.userType === 'admin'
      );

      if (!adminUser) {
        setError('Invalid email or password');
        setLoading(false);
        return;
      }

      // Check if admin is active and accepted
      const adminRecord = AdminDelegation.getAdminUser(adminUser.physicianId, adminUser.adminId);
      if (!adminRecord || !adminRecord.isActive || adminRecord.inviteStatus !== 'accepted') {
        setError('Admin account is not active or invitation not accepted');
        setLoading(false);
        return;
      }

      // Update last login
      AdminDelegation.updateAdminUser(adminUser.physicianId, adminUser.adminId, {
        lastLoginAt: new Date().toISOString()
      });

      // Log admin activity
      AdminDelegation.logAdminActivity(
        adminUser.physicianId,
        adminUser.adminId,
        'login',
        'Admin user logged in'
      );

      // Set current user and call onLogin
      UserStorage.setCurrentUser(adminUser);
      onLogin(adminUser);
      
    } catch (err) {
      setError('Login failed. Please try again.');
      logger.error('Login error:', err);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
            <p className="text-gray-600 mt-2">
              Sign in to manage your physician's portal
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t text-center">
            <p className="text-sm text-gray-600">
              Need help? Contact your physician's office
            </p>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600 mb-4">Admin Portal Features:</p>
          <div className="flex justify-center space-x-6 text-xs text-gray-500">
            <span>• Manage Meetings</span>
            <span>• Update Availability</span>
            <span>• Handle Rep Communications</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;